/**
 * CAD Call Card - create or edit a 911 CAD call.
 *
 * Modern port of the legacy 911CAD "New Call Card" screen, in the Castellan
 * dark theme. Received timestamp and control number are server-assigned on
 * create (shown read-only when editing). Type Call is required. Business and
 * Type Call are searchable pickers backed by the D1 reference lists. Unit
 * Assigned picks from active personnel. Dispatched/Arrived/Finished take HHMM
 * with a "now" button. Status is derived by the server from those time fields.
 */
import { useState, useEffect, useMemo, useRef } from 'react'
import { useCadStore, type CadCall, type CadCallInput, type CadBusiness } from '../../stores/cad'
import { useDesktopStore } from '../../stores/desktop'

interface Props {
  call?: CadCall | null
  onClose: () => void
}

function nowStamp(): string {
  // Full ET date + HH:MM:SS, e.g. "06/14/2026 14:32:07" — used for the
  // Dispatched / Arrived / Finished fields and the notes timestamp button.
  const parts = new Date().toLocaleString('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  })
  // en-US gives "06/14/2026, 14:32:07" — drop the comma
  return parts.replace(',', '')
}

export default function CallCardModal({ call, onClose }: Props) {
  const { types, businesses, createCall, updateCall, loadRefs } = useCadStore()
  const personnel = useDesktopStore(s => s.personnel)
  const isEdit = !!call

  const [form, setForm] = useState<CadCallInput>({
    location: call?.location ?? '',
    business_name: call?.business_name ?? '',
    beat: call?.beat ?? '',
    type_call: call?.type_call ?? '',
    additional_info: call?.additional_info ?? '',
    complainant: call?.complainant ?? '',
    contact_at: call?.contact_at ?? '',
    notes: call?.notes ?? '',
    unit_assigned: call?.unit_assigned ?? '',
    backup_unit: call?.backup_unit ?? '',
    dispatched: call?.dispatched ?? '',
    arrived: call?.arrived ?? '',
    finished: call?.finished ?? '',
    disposition: call?.disposition ?? '',
    flagged_for_incident: call?.flagged_for_incident ?? 0,
  })
  const [typeQuery, setTypeQuery] = useState('')
  const [bizQuery, setBizQuery] = useState('')
  const [showTypeList, setShowTypeList] = useState(false)
  const [showBizList, setShowBizList] = useState(false)
  const [saving, setSaving] = useState(false)

  // Close the searchable dropdowns when clicking anywhere outside them.
  const bizRef = useRef<HTMLDivElement>(null)
  const typeRef = useRef<HTMLDivElement>(null)
  const backupRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node
      if (bizRef.current && !bizRef.current.contains(t)) setShowBizList(false)
      if (typeRef.current && !typeRef.current.contains(t)) setShowTypeList(false)
      if (backupRef.current && !backupRef.current.contains(t)) setShowBackupList(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  useEffect(() => { loadRefs() }, [loadRefs])

  const set = (patch: Partial<CadCallInput>) => setForm(f => ({ ...f, ...patch }))

  const filteredTypes = useMemo(
    () => types.filter(t => t.toLowerCase().includes(typeQuery.toLowerCase())).slice(0, 60),
    [types, typeQuery],
  )
  const filteredBiz = useMemo(() => {
    const q = bizQuery.toLowerCase()
    const matches = businesses.filter(b => b.name.toLowerCase().includes(q))
    // Ranking: the primary address (Fair Oaks Mall) always floats to the top,
    // then names that START with the query (prefix matches), then the rest.
    // So typing "F" surfaces Fair Oaks Mall first, ahead of Foot Locker etc.
    return matches.sort((a, b) => {
      const aMain = a.name.toLowerCase() === 'fair oaks mall' ? 0 : 1
      const bMain = b.name.toLowerCase() === 'fair oaks mall' ? 0 : 1
      if (aMain !== bMain) return aMain - bMain
      const aPre = a.name.toLowerCase().startsWith(q) ? 0 : 1
      const bPre = b.name.toLowerCase().startsWith(q) ? 0 : 1
      if (aPre !== bPre) return aPre - bPre
      return a.name.localeCompare(b.name)
    }).slice(0, 60)
  }, [businesses, bizQuery])

  // Address ↔ business are linked. Picking a business fills its address;
  // typing/picking a known address fills the matching business name.
  // The stored column is `location` (the call's address line).
  function pickBusiness(b: CadBusiness) {
    set({ business_name: b.name, location: b.address || form.location, beat: b.beat || form.beat })
    setShowBizList(false)
  }
  function onAddressChange(value: string) {
    const match = businesses.find(b => b.address && b.address.toLowerCase() === value.trim().toLowerCase())
    if (match) set({ location: value, business_name: match.name, beat: match.beat || form.beat })
    else set({ location: value })
  }

  const units = useMemo(
    () => personnel.filter(p => p.name && p.callSign).map(p => ({ cs: p.callSign, name: p.name })),
    [personnel],
  )

  // Backup is stored as a comma-separated string in one column, but selected as
  // multiple units. Parse to a Set for the checklist, join back on change.
  const backupSet = useMemo(() => {
    const raw = form.backup_unit ?? ''
    return new Set(raw.split(',').map(s => s.trim()).filter(Boolean))
  }, [form.backup_unit])
  const [showBackupList, setShowBackupList] = useState(false)
  function toggleBackup(cs: string) {
    const next = new Set(backupSet)
    if (next.has(cs)) next.delete(cs); else next.add(cs)
    set({ backup_unit: Array.from(next).join(', ') })
  }

  async function handleSave() {
    if (!form.type_call) { alert('Type Call is required.'); return }
    setSaving(true)
    const result = isEdit && call ? await updateCall(call.id, form) : await createCall(form)
    setSaving(false)
    if (result) onClose()
  }

  const label: React.CSSProperties = { fontSize: 12, color: 'var(--text-muted)', marginBottom: 4, display: 'block' }
  const field: React.CSSProperties = {
    width: '100%', padding: '8px 10px', background: 'var(--bg-deep)', color: 'var(--text-primary)',
    border: '1px solid var(--border)', borderRadius: 6, fontSize: 14, boxSizing: 'border-box',
  }
  const row: React.CSSProperties = { marginBottom: 12 }
  const twoCol: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }

  function TimeField({ k, lbl }: { k: 'dispatched' | 'arrived' | 'finished'; lbl: string }) {
    return (
      <div>
        <label style={label}>{lbl}</label>
        <input
          style={{ ...field, cursor: 'pointer' }}
          value={form[k] ?? ''}
          placeholder="click to stamp"
          readOnly
          // Clicking inside the box stamps the current date + HH:MM:SS. Clicking
          // again refreshes it; to clear, the user can use the small × button.
          onClick={() => set({ [k]: nowStamp() } as Partial<CadCallInput>)}
        />
        {form[k] ? (
          <button type="button" onClick={() => set({ [k]: '' } as Partial<CadCallInput>)}
            style={{ marginTop: 3, fontSize: 10, background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 0 }}>
            clear
          </button>
        ) : null}
      </div>
    )
  }

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 12,
        width: 'min(680px, 100%)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Static header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <h2 style={{ margin: 0, fontSize: 18, color: 'var(--text-primary)' }}>
            {isEdit ? `Call ${call?.control_number}` : 'New Call Card'}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 22, cursor: 'pointer' }}>&times;</button>
        </div>

        {/* Scrolling body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '18px 20px' }}>

        {isEdit && (
          <div style={{ ...row, ...twoCol }}>
            <div><label style={label}>Received</label><div style={{ ...field, opacity: 0.7 }}>{call?.received_ts}</div></div>
            <div><label style={label}>Status</label><div style={{ ...field, opacity: 0.7 }}>{call?.status}</div></div>
          </div>
        )}

        <div style={row}>
          <label style={label}>Address / Location</label>
          <input style={field} value={form.location ?? ''} onChange={e => onAddressChange(e.target.value)}
            placeholder="start typing, or pick a business below" />
        </div>

        <div ref={bizRef} style={{ ...row, position: 'relative' }}>
          <label style={label}>Business Name</label>
          <input style={field} value={showBizList ? bizQuery : (form.business_name ?? '')}
            placeholder="Type to search businesses..."
            onFocus={() => { setShowBizList(true); setBizQuery('') }}
            onChange={e => setBizQuery(e.target.value)} />
          {showBizList && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 5, maxHeight: 200, overflowY: 'auto', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 6, marginTop: 2 }}>
              {filteredBiz.map(b => (
                <div key={b.name} onClick={() => pickBusiness(b)}
                  style={{ padding: '7px 10px', cursor: 'pointer', fontSize: 14, color: 'var(--text-primary)', borderBottom: '1px solid var(--border)' }}>
                  {b.name}
                </div>
              ))}
              {filteredBiz.length === 0 && <div style={{ padding: '7px 10px', fontSize: 13, color: 'var(--text-muted)' }}>No match - typed name will be used</div>}
            </div>
          )}
        </div>

        <div style={{ ...row, ...twoCol }}>
          <div>
            <label style={label}>Beat / Area</label>
            <input style={field} value={form.beat ?? ''} onChange={e => set({ beat: e.target.value })} />
          </div>
          <div ref={typeRef} style={{ position: 'relative' }}>
            <label style={label}>Type Call *</label>
            <input style={{ ...field, borderColor: form.type_call ? 'var(--border)' : 'var(--accent)' }}
              value={showTypeList ? typeQuery : (form.type_call ?? '')}
              placeholder="Type to search..."
              onFocus={() => { setShowTypeList(true); setTypeQuery('') }}
              onChange={e => setTypeQuery(e.target.value)} />
            {showTypeList && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 5, maxHeight: 220, overflowY: 'auto', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 6, marginTop: 2 }}>
                {filteredTypes.map(t => (
                  <div key={t} onClick={() => { set({ type_call: t }); setShowTypeList(false) }}
                    style={{ padding: '7px 10px', cursor: 'pointer', fontSize: 14, color: 'var(--text-primary)', borderBottom: '1px solid var(--border)' }}>
                    {t}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={row}>
          <label style={label}>Additional Info</label>
          <input style={field} value={form.additional_info ?? ''} onChange={e => set({ additional_info: e.target.value })} />
        </div>

        <div style={{ ...row, ...twoCol }}>
          <div><label style={label}>Complainant</label><input style={field} value={form.complainant ?? ''} onChange={e => set({ complainant: e.target.value })} /></div>
          <div><label style={label}>Contact At</label><input style={field} value={form.contact_at ?? ''} onChange={e => set({ contact_at: e.target.value })} /></div>
        </div>

        <div style={row}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label style={label}>Incident Notes</label>
            <button type="button"
              onClick={() => {
                // Stamp "MM/DD/YYYY HH:MM:SS - " at the cursor / end of the note,
                // each on its own line, so every entry is timestamped.
                const stamp = `${nowStamp()} - `
                const cur = form.notes ?? ''
                const next = cur ? `${cur}\n${stamp}` : stamp
                set({ notes: next })
              }}
              style={{ fontSize: 11, background: 'var(--bg-elevated)', color: 'var(--accent)', border: '1px solid var(--border)', borderRadius: 5, padding: '3px 9px', cursor: 'pointer', marginBottom: 4 }}>
              + timestamp
            </button>
          </div>
          <textarea style={{ ...field, minHeight: 90, resize: 'vertical', fontFamily: 'inherit' }} value={form.notes ?? ''} onChange={e => set({ notes: e.target.value })} />
        </div>

        <div style={{ ...row, ...twoCol }}>
          <div>
            <label style={label}>Unit Assigned</label>
            <select style={field} value={form.unit_assigned ?? ''} onChange={e => set({ unit_assigned: e.target.value })}>
              <option value="">- none -</option>
              {units.map(u => <option key={u.cs} value={u.cs}>{u.cs} - {u.name}</option>)}
            </select>
          </div>
          <div ref={backupRef} style={{ position: 'relative' }}>
            <label style={label}>Backup ({backupSet.size})</label>
            <div
              onClick={() => setShowBackupList(v => !v)}
              style={{ ...field, cursor: 'pointer', minHeight: 38, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
              {backupSet.size === 0
                ? <span style={{ color: 'var(--text-muted)' }}>- select one or more -</span>
                : Array.from(backupSet).map(cs => (
                    <span key={cs} style={{ fontSize: 12, background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 4, padding: '1px 6px', color: 'var(--text-primary)' }}>{cs}</span>
                  ))}
            </div>
            {showBackupList && (
              <div style={{ position: 'absolute', zIndex: 6, marginTop: 2, maxHeight: 200, overflowY: 'auto', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 6, minWidth: 200 }}>
                {units.map(u => (
                  <div key={u.cs} onClick={() => toggleBackup(u.cs)}
                    style={{ padding: '7px 10px', cursor: 'pointer', fontSize: 14, color: 'var(--text-primary)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" readOnly checked={backupSet.has(u.cs)} style={{ pointerEvents: 'none' }} />
                    {u.cs} - {u.name}
                  </div>
                ))}
                {units.length === 0 && <div style={{ padding: '7px 10px', fontSize: 13, color: 'var(--text-muted)' }}>No units on duty</div>}
              </div>
            )}
          </div>
        </div>

        <div style={{ ...row, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <TimeField k="dispatched" lbl="Dispatched" />
          <TimeField k="arrived" lbl="Arrived" />
          <TimeField k="finished" lbl="Finished" />
        </div>

        <div style={row}>
          <label style={label}>Disposition</label>
          <input style={field} value={form.disposition ?? ''} onChange={e => set({ disposition: e.target.value })} />
        </div>

        <div style={{ ...row, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 0 }}>
          <input type="checkbox" id="flag-inc" checked={!!form.flagged_for_incident}
            onChange={e => set({ flagged_for_incident: e.target.checked ? 1 : 0 })} />
          <label htmlFor="flag-inc" style={{ fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer' }}>
            Flag for Incident Report (serious event - link later)
          </label>
        </div>
        </div>{/* end scrolling body */}

        {/* Static footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 20px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
          <button onClick={onClose} style={{ padding: '9px 16px', background: 'var(--bg-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: '9px 18px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1, fontWeight: 600 }}>
            {saving ? 'Saving...' : isEdit ? 'Update Call' : 'Create Call'}
          </button>
        </div>
      </div>
    </div>
  )
}
